/**
 * Módulo de Compressão de Dados
 * 
 * Usado para comprimir payloads grandes antes de enviar ao Supabase
 * Reduz ~70-80% do tamanho dos dados usando GZIP
 */

import pako from 'pako';

/**
 * Comprime dados usando GZIP e converte para Base64
 * 
 * @param data - Qualquer dado serializável (objeto, array, etc)
 * @returns String Base64 comprimida
 * 
 * @example
 * const dados = { anos: [...], premissas: {...} };
 * const comprimido = compressData(dados);
 * // Tamanho reduzido em ~70-80%
 */
export const compressData = (data: unknown): string => {
  try {
    // 1. Serializar para JSON
    const jsonString = JSON.stringify(data);
    
    // 2. Comprimir com GZIP
    const compressed = pako.gzip(jsonString);
    
    // 3. Converter para Base64 (para armazenar como TEXT no banco)
    const base64 = btoa(String.fromCharCode(...compressed));
    
    return base64;
  } catch (error) {
    console.error('❌ Erro ao comprimir dados:', error);
    throw new Error('Falha na compressão de dados');
  }
};

/**
 * Descomprime dados GZIP em Base64
 * 
 * @param compressed - String Base64 comprimida
 * @returns Dados originais descomprimidos
 * 
 * @example
 * const dadosOriginais = decompressData(dadosComprimidos);
 */
export const decompressData = <T = unknown>(compressed: string): T => {
  try {
    // 1. Decodificar Base64
    const binaryString = atob(compressed);
    
    // 2. Converter para Uint8Array
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // 3. Descomprimir GZIP
    const decompressed = pako.ungzip(bytes);
    
    // 4. Converter para string e parsear JSON
    const jsonString = new TextDecoder().decode(decompressed);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('❌ Erro ao descomprimir dados:', error);
    throw new Error('Falha na descompressão de dados');
  }
};

/**
 * Calcula tamanho de dados em KB
 * 
 * @param data - Dados para medir
 * @returns Tamanho em KB
 */
export const getDataSize = (data: unknown): number => {
  const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
  return new Blob([jsonString]).size / 1024;
};

/**
 * Compara tamanhos original vs comprimido
 * 
 * @param data - Dados originais
 * @returns Estatísticas de compressão
 */
export const getCompressionStats = (data: unknown) => {
  const originalSize = getDataSize(data);
  const compressed = compressData(data);
  const compressedSize = getDataSize(compressed);
  const reduction = ((originalSize - compressedSize) / originalSize) * 100;
  
  return {
    originalKB: originalSize.toFixed(2),
    compressedKB: compressedSize.toFixed(2),
    reductionPercent: reduction.toFixed(1),
  };
};
